import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Backdrop,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import BorderedButton from '../../common/buttons/BorderedButton';
import PrimaryButton from '../../common/buttons/PrimaryButton';
import { fetchScheduleGradeTypes } from '../../../services/studentSchedule.service';
import { createTeacherFee, fetchTeacherFeeTypes, fetchTeacherFees } from '../../../services/teacherFee.service';
import { getApiErrorMessage, mapApiValidationErrors } from '../../../utils/apiError';

const initialForm = {
  gradeId: '',
  feeFor: '',
  fee: '',
};

const initialErrors = {
  gradeId: '',
  feeFor: '',
  fee: '',
};

const fieldStyles = {
  '& .MuiOutlinedInput-root': {
    color: 'white',
    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
    '&:hover fieldset': { borderColor: '#F6911D' },
    '&.Mui-focused fieldset': { borderColor: '#F6911D' },
  },
  '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.6)' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#F6911D' },
  '& .MuiFormHelperText-root': { color: 'rgba(255, 255, 255, 0.75)' },
  '& .MuiFormHelperText-root.Mui-error': { color: '#fca5a5' },
  '& .MuiSvgIcon-root': { color: 'rgba(255, 255, 255, 0.6)' },
};

const formatFee = (value) => {
  if (value === '' || value === null || value === undefined) return '';

  const normalized = Number(String(value).replace(/,/g, ''));

  if (!Number.isFinite(normalized)) return value ?? '';

  return normalized.toLocaleString('en-US');
};

const parseFee = (value) => Number(String(value).replace(/,/g, '').trim());

const sortBySelectedGradeAndFeeTypes = (fees, gradeTypes, feeTypes) => {
  const gradeOrder = new Map(gradeTypes.map((gradeType, index) => [String(gradeType.id), index]));
  const feeTypeOrder = new Map(feeTypes.map((feeType, index) => [String(feeType.code), index]));

  return [...fees].sort((a, b) => {
    const aGradeOrder = gradeOrder.has(String(a.gradeId)) ? gradeOrder.get(String(a.gradeId)) : Number.MAX_SAFE_INTEGER;
    const bGradeOrder = gradeOrder.has(String(b.gradeId)) ? gradeOrder.get(String(b.gradeId)) : Number.MAX_SAFE_INTEGER;

    if (aGradeOrder !== bGradeOrder) return aGradeOrder - bGradeOrder;

    const aFeeTypeOrder = feeTypeOrder.has(String(a.feeFor)) ? feeTypeOrder.get(String(a.feeFor)) : Number.MAX_SAFE_INTEGER;
    const bFeeTypeOrder = feeTypeOrder.has(String(b.feeFor)) ? feeTypeOrder.get(String(b.feeFor)) : Number.MAX_SAFE_INTEGER;

    if (aFeeTypeOrder !== bFeeTypeOrder) return aFeeTypeOrder - bFeeTypeOrder;

    return String(a.gradeName || '').localeCompare(String(b.gradeName || ''));
  });
};

export default function TeacherFeeModal({ open, teacher, onClose, onSuccess }) {
  const teacherUserId = teacher?.id ?? null;
  const [gradeTypes, setGradeTypes] = useState([]);
  const [feeTypes, setFeeTypes] = useState([]);
  const [fees, setFees] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState(initialErrors);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [submitError, setSubmitError] = useState('');

  const selectedFee = useMemo(
    () =>
      fees.find(
        (fee) => String(fee.gradeId) === String(form.gradeId) && String(fee.feeFor) === String(form.feeFor),
      ) || null,
    [fees, form.gradeId, form.feeFor],
  );

  const resetModal = () => {
    setGradeTypes([]);
    setFeeTypes([]);
    setFees([]);
    setForm(initialForm);
    setErrors(initialErrors);
    setLoading(false);
    setSubmitting(false);
    setLoadError('');
    setSubmitError('');
  };

  const loadData = async (targetTeacherUserId, isCancelled = () => false) => {
    setLoading(true);
    setLoadError('');

    const [gradeTypesResult, feeTypesResult, feesResult] = await Promise.allSettled([
      fetchScheduleGradeTypes(),
      fetchTeacherFeeTypes(),
      fetchTeacherFees(targetTeacherUserId),
    ]);

    if (isCancelled()) return;

    if (gradeTypesResult.status === 'fulfilled') {
      setGradeTypes(gradeTypesResult.value);
    } else {
      setGradeTypes([]);
    }

    if (feeTypesResult.status === 'fulfilled') {
      setFeeTypes(feeTypesResult.value);
    } else {
      setFeeTypes([]);
    }

    if (feesResult.status === 'fulfilled') {
      setFees(
        sortBySelectedGradeAndFeeTypes(
          feesResult.value,
          gradeTypesResult.status === 'fulfilled' ? gradeTypesResult.value : [],
          feeTypesResult.status === 'fulfilled' ? feeTypesResult.value : [],
        ),
      );
    } else {
      setFees([]);
    }

    const failures = [];
    if (gradeTypesResult.status === 'rejected') failures.push('grade type list');
    if (feeTypesResult.status === 'rejected') failures.push('fee for options');
    if (feesResult.status === 'rejected') failures.push('fee configurations');

    if (failures.length > 0) {
      setLoadError(`Unable to load ${failures.join(' and ')}. Please try again.`);
    }

    setLoading(false);
  };

  useEffect(() => {
    if (!open || !teacherUserId) {
      resetModal();
      return undefined;
    }

    let cancelled = false;

    const startLoad = async () => {
      await loadData(teacherUserId, () => cancelled);
      if (cancelled) return;

      setForm(initialForm);
      setErrors(initialErrors);
      setSubmitError('');
    };

    startLoad();

    return () => {
      cancelled = true;
    };
  }, [open, teacherUserId]);

  const handleClose = () => {
    if (submitting) return;

    resetModal();
    onClose?.();
  };

  const handleGradeChange = (event) => {
    const gradeId = event.target.value;

    setForm({
      gradeId,
      feeFor: '',
      fee: '',
    });
    setErrors(initialErrors);
    setSubmitError('');
  };

  const handleFeeForChange = (event) => {
    const feeFor = event.target.value;
    setForm((current) => {
      const existingFee =
        fees.find((fee) => String(fee.gradeId) === String(current.gradeId) && String(fee.feeFor) === String(feeFor)) ||
        null;

      return {
        ...current,
        feeFor,
        fee: existingFee ? String(existingFee.fee ?? '') : '',
      };
    });
    if (errors.feeFor) setErrors((current) => ({ ...current, feeFor: '' }));
    setSubmitError('');
  };

  const handleFeeChange = (event) => {
    setForm((current) => ({ ...current, fee: event.target.value }));
    if (errors.fee) setErrors((current) => ({ ...current, fee: '' }));
  };

  const validate = () => {
    const nextErrors = {
      gradeId: form.gradeId ? '' : 'Grade Type is required.',
      feeFor: form.feeFor ? '' : 'Fee for is required.',
      fee: form.fee !== '' ? '' : 'Fee is required.',
    };

    const feeValue = parseFee(form.fee);
    if (nextErrors.fee === '' && (!Number.isFinite(feeValue) || feeValue < 0)) {
      nextErrors.fee = 'Enter a valid fee amount.';
    }

    setErrors(nextErrors);
    return !Object.values(nextErrors).some(Boolean);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (submitting || !teacherUserId || !validate()) return;

    const payload = {
      teacher_user_id: teacherUserId,
      grade_type_id: form.gradeId,
      fee_type_code: form.feeFor,
      fee: parseFee(form.fee),
    };

    setSubmitting(true);
    setSubmitError('');

    try {
      const response = await createTeacherFee(payload);
      await loadData(teacherUserId);
      setForm(initialForm);
      setErrors(initialErrors);
      onSuccess?.(response);
    } catch (error) {
      if (error?.response?.status === 422) {
        const mappedErrors = mapApiValidationErrors(error.response?.data);

        setErrors((current) => ({
          ...current,
          gradeId: mappedErrors.grade_id || mappedErrors.gradeId || current.gradeId,
          feeFor:
            mappedErrors.fee_for || mappedErrors.fee_type_code || mappedErrors.feeFor || current.feeFor,
          fee: mappedErrors.fee || mappedErrors.amount || mappedErrors.value || current.fee,
        }));
      }

      setSubmitError(getApiErrorMessage(error, 'Unable to save teacher fee. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditClick = (feeConfig) => {
    setForm({
      gradeId: feeConfig.gradeId,
      feeFor: feeConfig.feeFor,
      fee: String(feeConfig.fee ?? ''),
    });
    setErrors(initialErrors);
    setSubmitError('');
  };

  if (!teacher) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(8px)',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
          },
        },
        paper: {
          sx: {
            background: 'linear-gradient(158deg, rgba(17, 24, 39, 1) 0%, rgba(15, 15, 18, 1) 100%)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
          },
        },
      }}
    >
      <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
        <div className="mb-5 flex items-start justify-between gap-4">
          <div>
            <h3 className="text-xl font-semibold text-white">Teacher Fee Configuration</h3>
            <p className="mt-1 text-sm text-gray-400">
              {teacher.firstName} {teacher.lastName}
            </p>
          </div>
          <IconButton onClick={handleClose} sx={{ color: 'rgba(255,255,255,0.7)' }}>
            <CloseIcon />
          </IconButton>
        </div>

        {loadError ? (
          <Alert severity="error" sx={{ mb: 3, backgroundColor: 'rgba(220,38,38,0.12)', color: '#fca5a5' }}>
            {loadError}
          </Alert>
        ) : null}

        {submitError ? (
          <Alert severity="error" sx={{ mb: 3, backgroundColor: 'rgba(220,38,38,0.12)', color: '#fca5a5' }}>
            {submitError}
          </Alert>
        ) : null}

        <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
          <form onSubmit={handleSubmit} className="space-y-5 rounded-xl border border-white/10 bg-white/5 p-4">
            <FormControl fullWidth error={Boolean(errors.gradeId)} disabled={loading} sx={fieldStyles}>
              <InputLabel>Grade Type *</InputLabel>
              <Select value={form.gradeId} label="Grade Type *" onChange={handleGradeChange}>
                <MenuItem value="" disabled>
                  {loading ? 'Loading grade types...' : 'Select grade type'}
                </MenuItem>
                {gradeTypes.map((gradeType) => (
                  <MenuItem key={gradeType.id} value={gradeType.id}>
                    {gradeType.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.gradeId ? <FormHelperText>{errors.gradeId}</FormHelperText> : null}
            </FormControl>

            <FormControl fullWidth error={Boolean(errors.feeFor)} disabled={loading || !form.gradeId} sx={fieldStyles}>
              <InputLabel>Fee for *</InputLabel>
              <Select value={form.feeFor} label="Fee for *" onChange={handleFeeForChange}>
                <MenuItem value="" disabled>
                  {loading ? 'Loading fee types...' : 'Select fee for'}
                </MenuItem>
                {feeTypes.map((feeType) => (
                  <MenuItem key={feeType.code} value={feeType.code}>
                    {feeType.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.feeFor ? <FormHelperText>{errors.feeFor}</FormHelperText> : null}
            </FormControl>

            <TextField
              label="Fee *"
              value={form.fee}
              onChange={handleFeeChange}
              disabled={loading}
              type="number"
              inputProps={{ min: 0, step: 1 }}
              fullWidth
              error={Boolean(errors.fee)}
              helperText={errors.fee || 'Enter the fee amount.'}
              sx={fieldStyles}
            />

            <div className="rounded-lg border border-white/10 bg-black/20 p-3 text-sm text-gray-300">
              <p className="font-medium text-white">Mode</p>
              <p className="mt-1">
                {selectedFee ? 'Edit Fee' : 'Add Fee'}
              </p>
            </div>

            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <BorderedButton onClick={handleClose} disabled={submitting} sx={{ minWidth: 120 }}>
                Cancel
              </BorderedButton>
              <PrimaryButton type="submit" disabled={submitting || loading} sx={{ minWidth: 140 }}>
                {submitting ? <CircularProgress size={18} sx={{ color: 'white' }} /> : selectedFee ? 'Update Fee' : 'Add Fee'}
              </PrimaryButton>
            </div>
          </form>

          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <h4 className="text-lg font-semibold text-white">Existing Fees</h4>
                <p className="text-sm text-gray-400">Grade type to fee mappings for this teacher.</p>
              </div>
            </div>

            {loading ? (
              <div className="flex min-h-[220px] items-center justify-center">
                <CircularProgress sx={{ color: '#F6911D' }} />
              </div>
            ) : fees.length > 0 ? (
              <div className="overflow-x-auto">
                <Table size="small" sx={{ '& th, & td': { borderColor: 'rgba(255,255,255,0.08)', color: 'white' } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Grade Type</TableCell>
                      <TableCell>Fee For</TableCell>
                      <TableCell align="right">Fee</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fees.map((fee) => (
                      <TableRow key={fee.id} hover>
                        <TableCell>
                          {fee.gradeName ||
                            gradeTypes.find((gradeType) => String(gradeType.id) === String(fee.gradeId))?.name ||
                            'Unknown'}
                        </TableCell>
                        <TableCell>
                          {fee.feeForLabel || feeTypes.find((feeType) => String(feeType.code) === String(fee.feeFor))?.name ||
                            fee.feeFor ||
                            'Unknown'}
                        </TableCell>
                        <TableCell align="right">{formatFee(fee.fee)}</TableCell>
                        <TableCell align="right">
                          <button
                            type="button"
                            onClick={() => handleEditClick(fee)}
                            className="rounded-md border border-orange-500/30 px-3 py-1.5 text-sm font-medium text-orange-300 transition hover:bg-orange-500/10 hover:text-white"
                          >
                            Edit
                          </button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="flex min-h-[220px] items-center justify-center rounded-lg border border-dashed border-white/10 text-center">
                <p className="text-sm text-gray-400">No fee configurations found for this teacher.</p>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
